import { Sparkles, ArrowRight, Mail, MapPin, Palette, Tent, Globe2, AtSign, X, Plus } from 'lucide-react';
import { C } from '../config/constants';
import Slider from './Slider';
import { resolveImg } from '../utils/img';
import { useElementParallax } from '../hooks';
import { EditableText, EditableImage, EditableSliderManager, EditableMosaicPhoto } from './EditableField';

/* ── Helpers de disseny reutilitzats ── */
function Eyebrow({ children, dark }) {
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
      background: dark ? 'rgba(255,255,255,0.15)' : C.grocClar,
      color: dark ? '#fff' : '#8a6d00',
      padding: '0.4rem 1rem', borderRadius: '999px',
      fontSize: '0.8rem', fontWeight: 700,
      marginBottom: '1rem', fontFamily: "'Quicksand', sans-serif",
    }}>
      <Sparkles size={14} strokeWidth={2.5} />{children}
    </div>
  );
}

const ACT_ICONS = [Palette, Tent, Globe2];
const MOSAIC_MIN_SLOTS = 8;

/* ── Foto de fons amb parallax subtil, relatiu a la secció (no al scroll global) ── */
function ParallaxBg({ foto, opacity = 0.14, tint, factor = 0.1 }) {
  const [ref, offset] = useElementParallax(factor);
  if (!foto) return null;
  return (
    <div ref={ref} style={{ position:'absolute', inset:0, overflow:'hidden', pointerEvents:'none' }}>
      <img src={resolveImg(foto)} alt="" style={{
        position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover',
        transform:`translateY(${offset}px) scale(1.15)`, willChange:'transform',
        filter:'saturate(1.1)', opacity,
      }}/>
      {tint && <div style={{ position:'absolute', inset:0, background:tint }}/>}
    </div>
  );
}

/* ── Cinta contínua de fotos (marquee), pur CSS, molt eficient ── */
function MarqueeRow({ fotos, direction = 'left', duration = 32 }) {
  if (!fotos.length) return null;
  const doubled = fotos.length < 4 ? [...fotos, ...fotos, ...fotos, ...fotos] : [...fotos, ...fotos];
  return (
    <div className="marquee-row">
      <div className={`marquee-track ${direction === 'right' ? 'marquee-right' : ''}`}
        style={{ '--marquee-duration': `${duration}s` }}>
        {doubled.map((f, i) => (
          <img key={i} src={resolveImg(f)} alt="" className="marquee-photo" />
        ))}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   INTRO — Benvinguda amb mosaic/marquee de fotos i parallax subtil
════════════════════════════════════════════════════ */
export function Intro({ data, isAdmin, onChange }) {
  const fotos = data?.fotos || [];
  const goTo = id => document.getElementById(id)?.scrollIntoView({ behavior:'smooth' });

  const setFotos = next => onChange({ ...(data || {}), fotos: next });

  const handleReplace = (idx, src) => {
    const next = [...fotos];
    if (idx < next.length) next[idx] = src; else next.push(src);
    setFotos(next);
  };
  const handleRemove = idx => setFotos(fotos.filter((_, i) => i !== idx));

  const rowA = fotos.filter((_, i) => i % 2 === 0);
  const rowB = fotos.filter((_, i) => i % 2 === 1);
  const slotCount = Math.max(MOSAIC_MIN_SLOTS, fotos.length);

  return (
    <div style={{ position: 'relative', overflow: 'hidden', padding: '4.5rem 1.5rem 5.5rem' }}>
      <ParallaxBg foto={fotos[0]} opacity={0.16} factor={0.1}
        tint="rgba(255,248,225,0.7)"/>

      <div className="anim-fadeUp" style={{
        position:'relative', maxWidth: 640, margin: '0 auto', textAlign: 'center',
      }}>
        <img src="assets/logo.png" alt="Logo El Casalot" style={{
          height: '116px', marginBottom: '1.5rem',
          filter: 'drop-shadow(0 8px 20px rgba(0,51,102,0.18))',
        }}/>
        <h1 className="font-title" style={{
          fontSize: 'clamp(2.2rem, 5.5vw, 3.4rem)', fontWeight: 600,
          color: C.blau, lineHeight: 1.15,
          marginBottom: '1rem',
        }}>
          Esplai El <span style={{ position:'relative', display:'inline-block' }}>
            Casalot
            <svg viewBox="0 0 200 16" style={{ position:'absolute', bottom:-4, left:0, width:'100%', height:14 }}>
              <path d="M2,10 Q50,2 100,8 T198,6" stroke={C.groc} strokeWidth="7" fill="none" strokeLinecap="round"/>
            </svg>
          </span>
        </h1>
        <p style={{
          fontSize: '1.15rem', color: C.textMig, lineHeight: 1.6,
          marginBottom: '2.25rem', fontWeight: 500,
        }}>
          Educació en el lleure, valors i comunitat.<br/>
          Un espai on els infants creixen jugant.
        </p>

        <div style={{ display:'flex', gap:'0.9rem', justifyContent:'center', flexWrap:'wrap' }}>
          <button onClick={() => goTo('qui-som')} style={{
            display:'flex', alignItems:'center', gap:'0.4rem',
            background: C.blau, color: '#fff', border:'none',
            borderRadius: '999px', padding:'0.9rem 1.8rem',
            fontWeight: 700, fontSize:'1rem', cursor:'pointer', fontFamily:"'Quicksand',sans-serif",
            boxShadow:'0 10px 26px rgba(0,51,102,0.22)', transition:'all 0.25s',
          }}
            onMouseEnter={e => e.currentTarget.style.transform='translateY(-3px)'}
            onMouseLeave={e => e.currentTarget.style.transform='none'}
          >Descobreix qui som <ArrowRight size={18}/></button>

          <button onClick={() => goTo('contacte')} style={{
            display:'flex', alignItems:'center', gap:'0.4rem',
            background: '#fff', color: C.blau, border:`2px solid ${C.groc}`,
            borderRadius: '999px', padding:'0.85rem 1.8rem',
            fontWeight: 700, fontSize:'1rem', cursor:'pointer', fontFamily:"'Quicksand',sans-serif",
            transition:'all 0.25s',
          }}
            onMouseEnter={e => { e.currentTarget.style.background=C.grocClar; }}
            onMouseLeave={e => { e.currentTarget.style.background='#fff'; }}
          >Contacta'ns <Mail size={17}/></button>
        </div>
      </div>

      {/* Mosaic (admin, editable) o marquee (públic, en moviment) */}
      <div style={{ position:'relative', marginTop: '3rem' }}>
        {isAdmin ? (
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <p style={{ textAlign:'center', fontSize:'0.8rem', color:C.textClar, marginBottom:'0.75rem' }}>
              Fotos de la portada — clica una targeta per afegir o canviar una foto
            </p>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(88px,1fr))', gap:'0.6rem' }}>
              {Array.from({ length: slotCount }).map((_, i) => (
                <EditableMosaicPhoto key={i}
                  src={i < fotos.length ? resolveImg(fotos[i]) : null}
                  onReplace={src => handleReplace(i, src)}
                  onRemove={() => handleRemove(i)}
                  isAdmin={isAdmin}
                  style={{ width:'100%', aspectRatio:'1', objectFit:'cover', borderRadius:14 }}
                />
              ))}
            </div>
          </div>
        ) : (
          fotos.length > 0 && (
            <div className="marquee-wrap">
              <MarqueeRow fotos={rowA.length ? rowA : fotos} direction="left" duration={34}/>
              {rowB.length > 0 && <MarqueeRow fotos={rowB} direction="right" duration={30}/>}
            </div>
          )
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   QUI SOM — Text + pills editables + col·lage lateral
════════════════════════════════════════════════════ */
export function QuiSom({ data, isAdmin, onChange, fotos = [] }) {
  const upd = k => v => onChange({ ...data, [k]: v });
  const pills = data.pills || ['Laic','Inclusiu','Participatiu','Divertit'];
  const pillColors = [C.groc, C.verd, C.cel, C.rosa];
  const sidePhotos = fotos.slice(1, 3);

  const editPill = (i, val) => {
    const next = [...pills]; next[i] = val; upd('pills')(next);
  };
  const removePill = i => upd('pills')(pills.filter((_, idx) => idx !== i));
  const addPill = () => upd('pills')([...pills, 'Nou valor']);

  return (
    <div id="qui-som" className="reveal mobile-pad" style={{ padding: '5rem 1.5rem' }}>
      <div className="col-2" style={{
        maxWidth: 1050, margin: '0 auto',
        display:'grid', gridTemplateColumns: sidePhotos.length ? '1.15fr 0.85fr' : '1fr',
        gap: '3rem', alignItems: 'center',
      }}>
        <div style={{ textAlign: sidePhotos.length ? 'left' : 'center' }}>
          <Eyebrow>L'esplai</Eyebrow>
          <EditableText tag="h2" value={data.titol} onSave={upd('titol')} isAdmin={isAdmin}
            style={{ fontFamily:"'Fredoka',sans-serif", fontSize:'clamp(1.9rem,3.2vw,2.5rem)', fontWeight:600,
              color:C.blau, marginBottom:'1.1rem' }}/>
          <EditableText tag="p" value={data.text} onSave={upd('text')} isAdmin={isAdmin} multiline
            style={{ fontSize:'1.08rem', lineHeight:1.85, color:C.textMig, marginBottom:'2rem' }}/>

          {/* Pills editables */}
          <div style={{ display:'flex', gap:'0.6rem', flexWrap:'wrap',
            justifyContent: sidePhotos.length ? 'flex-start' : 'center' }}>
            {pills.map((pill, i) => (
              <div key={i} style={{ position:'relative', display:'inline-flex' }}>
                <div style={{
                  background: `${pillColors[i % 4]}22`,
                  color: C.blau, fontWeight: 700, fontSize: '0.9rem',
                  padding: isAdmin ? '0.55rem 1.8rem 0.55rem 1.2rem' : '0.55rem 1.2rem',
                  borderRadius: '999px',
                  border: `2px solid ${pillColors[i % 4]}66`,
                }}>
                  {isAdmin
                    ? <EditableText tag="span" value={pill} isAdmin={isAdmin}
                        onSave={v => editPill(i, v)} style={{ color:C.blau, fontWeight:700 }}/>
                    : pill}
                </div>
                {isAdmin && (
                  <button onClick={() => removePill(i)} style={{
                    position:'absolute', right:4, top:'50%', transform:'translateY(-50%)',
                    background:'none', border:'none', cursor:'pointer', color:C.blau,
                    opacity:0.5, padding:2, display:'flex',
                  }}><X size={14}/></button>
                )}
              </div>
            ))}
            {isAdmin && (
              <button onClick={addPill} style={{
                display:'flex', alignItems:'center', gap:4,
                border:`2px dashed ${C.blau}55`, borderRadius:'999px',
                padding:'0.5rem 1rem', background:'transparent', color:C.blau,
                fontWeight:700, fontSize:'0.85rem', cursor:'pointer', fontFamily:'inherit',
              }}><Plus size={14}/> Afegir</button>
            )}
          </div>
        </div>

        {/* Col·lage lateral de fotos reals */}
        {sidePhotos.length > 0 && (
          <div className="hide-mobile" style={{ position:'relative', height:280 }}>
            {sidePhotos.map((f, i) => (
              <img key={i} src={resolveImg(f)} alt="" style={{
                width: i===0 ? '72%' : '58%', aspectRatio:'1', objectFit:'cover',
                borderRadius: 24, border:'5px solid #fff', boxShadow:C.shadowLg,
                position:'absolute',
                top: i===0 ? 0 : '38%', right: i===0 ? '8%' : 0,
                transform: i===0 ? 'rotate(-3deg)' : 'rotate(4deg)',
                zIndex: i===0 ? 1 : 2,
              }}/>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   QUÈ FEM — Slider + activitats destacades
════════════════════════════════════════════════════ */
export function QueFem({ data, isAdmin, onChange }) {
  const upd = k => v => onChange({ ...data, [k]: v });
  const activitats = data.activitats || [];

  return (
    <div id="que-fem" className="reveal mobile-pad" style={{ padding: '5rem 1.5rem' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div className="col-2" style={{
          display:'grid', gridTemplateColumns:'1fr 1fr',
          gap:'3rem', alignItems:'center', marginBottom:'3rem',
        }}>
          <div>
            <Eyebrow>Activitats</Eyebrow>
            <EditableText tag="h2" value={data.titol} onSave={upd('titol')} isAdmin={isAdmin}
              style={{ fontFamily:"'Fredoka',sans-serif", fontSize:'clamp(1.9rem,3.2vw,2.5rem)', fontWeight:600,
                color:C.blau, marginBottom:'1rem' }}/>
            <EditableText tag="p" value={data.text} onSave={upd('text')} isAdmin={isAdmin} multiline
              style={{ fontSize:'1.05rem', lineHeight:1.8, color:C.textMig }}/>
            <EditableSliderManager fotos={data.fotos||[]} onSave={upd('fotos')} isAdmin={isAdmin}/>
          </div>
          <Slider fotos={data.fotos}/>
        </div>

        {/* Activitats destacades */}
        <div className="col-3" style={{
          display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'1.25rem',
        }}>
          {activitats.map((act, i) => {
            const Icon = ACT_ICONS[i % ACT_ICONS.length];
            return (
              <div key={i} style={{
                background:'#fff', borderRadius:C.radius, padding:'1.75rem',
                boxShadow:C.shadow, transition:'transform 0.25s, box-shadow 0.25s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow=C.shadowMd; }}
                onMouseLeave={e => { e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow=C.shadow; }}
              >
                <div style={{
                  width:52, height:52, borderRadius:'18px', background:C.blauClar,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  marginBottom:'0.85rem', color:C.blau,
                }}><Icon size={24} strokeWidth={2}/></div>
                {isAdmin ? (
                  <>
                    <EditableText tag="div" value={act.titol} isAdmin={isAdmin}
                      onSave={nv => { const na=[...activitats]; na[i]={...na[i],titol:nv}; upd('activitats')(na); }}
                      style={{ fontFamily:"'Fredoka',sans-serif", fontWeight:600, color:C.blau, fontSize:'1.05rem', marginBottom:'0.3rem' }}/>
                    <EditableText tag="div" value={act.desc} isAdmin={isAdmin} multiline
                      onSave={nv => { const na=[...activitats]; na[i]={...na[i],desc:nv}; upd('activitats')(na); }}
                      style={{ fontSize:'0.9rem', color:C.textMig, lineHeight:1.6 }}/>
                  </>
                ) : (
                  <>
                    <div style={{ fontFamily:"'Fredoka',sans-serif", fontWeight:600, color:C.blau, fontSize:'1.05rem', marginBottom:'0.3rem' }}>{act.titol}</div>
                    <div style={{ fontSize:'0.9rem', color:C.textMig, lineHeight:1.6 }}>{act.desc}</div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   ON SOM — Mapa amb card d'informació
════════════════════════════════════════════════════ */
export function OnSom({ data, isAdmin, onChange }) {
  const upd = k => v => onChange({ ...data, [k]: v });

  return (
    <div id="on-som" className="reveal mobile-pad" style={{ padding: '5rem 1.5rem' }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign:'center', marginBottom:'2.5rem' }}>
          <Eyebrow>Ubicació</Eyebrow>
          <div style={{ display:'flex', justifyContent:'center' }}>
            <EditableText tag="h2" value={data.titol} onSave={upd('titol')} isAdmin={isAdmin}
              style={{ fontFamily:"'Fredoka',sans-serif", fontSize:'clamp(1.9rem,3.2vw,2.5rem)', fontWeight:600, color:C.blau }}/>
          </div>
        </div>

        <div style={{ position:'relative', borderRadius:C.radius, overflow:'hidden', boxShadow:C.shadowLg }}>
          <EditableImage src={data.mapaUrl} onSave={upd('mapaUrl')} isAdmin={isAdmin} alt="Mapa"
            imgStyle={{ width:'100%', height:'340px', objectFit:'cover', display:'block' }}/>

          <div style={{ position:'absolute', inset:0, pointerEvents:'none',
            background:'linear-gradient(to top, rgba(0,20,50,0.35), transparent 50%)' }}/>

          <div style={{
            position:'absolute', bottom:'1.25rem', left:'1.25rem', right:'1.25rem',
            display:'flex', flexWrap:'wrap', gap:'1rem', justifyContent:'space-between', alignItems:'center',
          }}>
            <div style={{
              background:'rgba(255,255,255,0.95)', backdropFilter:'blur(12px)',
              borderRadius:C.radiusSm, padding:'0.9rem 1.3rem',
              display:'flex', alignItems:'center', gap:'0.75rem',
              boxShadow:C.shadowMd,
            }}>
              <div style={{ width:38, height:38, borderRadius:12, background:C.grocClar,
                display:'flex', alignItems:'center', justifyContent:'center', color:'#8a6d00', flexShrink:0 }}>
                <MapPin size={19}/>
              </div>
              <div>
                <div style={{ fontFamily:"'Fredoka',sans-serif", fontWeight:600, color:C.blau, fontSize:'0.98rem' }}>Esplai El Casalot</div>
                <div style={{ fontSize:'0.8rem', color:C.textClar }}>Roda de Ter</div>
              </div>
            </div>

            {isAdmin
              ? <EditableText tag="span" value={data.mapsLink} onSave={upd('mapsLink')} isAdmin={isAdmin}
                  style={{ fontSize:'0.75rem', color:'#fff', background:'rgba(0,0,0,0.5)', padding:'4px 10px', borderRadius:10 }}/>
              : <a href={data.mapsLink} target="_blank" rel="noopener noreferrer" style={{
                  background: C.groc, color: C.blau, textDecoration:'none',
                  fontWeight: 700, fontSize:'0.9rem', padding:'0.75rem 1.4rem',
                  borderRadius: '999px', boxShadow: C.shadowMd,
                }}>Veure a Maps →</a>
            }
          </div>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   ESPLAC — Valors + foto de fons amb overlay
════════════════════════════════════════════════════ */
const VALOR_STYLES = [
  { bg:'#e0f2fe', border:'#38bdf8', color:'#075985', dot:'#38bdf8' },
  { bg:'#dcfce7', border:'#4ade80', color:'#166534', dot:'#4ade80' },
  { bg:'#fef9c3', border:'#facc15', color:'#854d0e', dot:'#eab308' },
  { bg:'#fce7f3', border:'#f472b6', color:'#9d174d', dot:'#f472b6' },
];

export function Esplac({ data, isAdmin, onChange, bgFoto }) {
  const upd = k => v => onChange({ ...data, [k]: v });

  return (
    <div id="esplac" className="reveal mobile-pad" style={{ padding:'5rem 1.5rem', position:'relative' }}>
      <ParallaxBg foto={bgFoto} opacity={0.1} factor={0.08} tint="rgba(219,231,251,0.55)"/>

      <div style={{ maxWidth: 900, margin:'0 auto', position:'relative' }}>
        <div style={{ textAlign:'center', marginBottom:'2rem' }}>
          <Eyebrow>La nostra xarxa</Eyebrow>
          <div style={{ display:'flex', justifyContent:'center' }}>
            <EditableText tag="h2" value={data.titol} onSave={upd('titol')} isAdmin={isAdmin}
              style={{ fontFamily:"'Fredoka',sans-serif", fontSize:'clamp(1.9rem,3.2vw,2.5rem)', fontWeight:600, color:C.blau }}/>
          </div>
        </div>

        <EditableText tag="p" value={data.intro} onSave={upd('intro')} isAdmin={isAdmin} multiline
          style={{ fontSize:'1.05rem', lineHeight:1.85, color:C.textMig,
            textAlign:'center', marginBottom:'2.25rem', maxWidth:680, marginLeft:'auto', marginRight:'auto' }}/>

        <EditableText tag="p" value={data.valorsIntro} onSave={upd('valorsIntro')} isAdmin={isAdmin}
          style={{ fontFamily:"'Fredoka',sans-serif", fontWeight:600, color:C.blau, fontSize:'1.05rem', marginBottom:'1.1rem', textAlign:'center' }}/>

        <div className="col-2" style={{
          display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:'0.9rem', marginBottom:'2rem',
        }}>
          {(data.valors||[]).map((v,i) => {
            const s = VALOR_STYLES[i % 4];
            return (
              <div key={i} style={{
                background:s.bg, border:`2px solid ${s.border}`,
                borderRadius:C.radiusSm, padding:'1.2rem 1.4rem',
                display:'flex', gap:'0.75rem', alignItems:'flex-start',
                transition:'transform 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform='translateY(-3px)'}
                onMouseLeave={e => e.currentTarget.style.transform='none'}
              >
                <span style={{ width:9, height:9, borderRadius:'50%', background:s.dot,
                  flexShrink:0, marginTop:6 }}/>
                {isAdmin ? (
                  <EditableText tag="span" value={v} isAdmin={isAdmin}
                    onSave={nv => { const nx=[...data.valors]; nx[i]=nv; upd('valors')(nx); }}
                    style={{ color:s.color, fontWeight:600, fontSize:'0.94rem', lineHeight:1.5 }}/>
                ) : (
                  <span style={{ color:s.color, fontWeight:600, fontSize:'0.94rem', lineHeight:1.5 }}>{v}</span>
                )}
              </div>
            );
          })}
        </div>

        <EditableText tag="p" value={data.outro} onSave={upd('outro')} isAdmin={isAdmin} multiline
          style={{ fontSize:'0.92rem', color:C.textClar, fontStyle:'italic',
            textAlign:'center', lineHeight:1.7 }}/>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════
   CONTACTE — Fons navy pla + foto (color sòlid des d'App.jsx)
════════════════════════════════════════════════════ */
export function Contacte({ data, isAdmin, onChange, bgFoto }) {
  const upd = k => v => onChange({ ...data, [k]: v });

  return (
    <div id="contacte" className="reveal mobile-pad" style={{
      padding: '5rem 1.5rem', position:'relative', overflow:'hidden',
    }}>
      <ParallaxBg foto={bgFoto} opacity={0.14} factor={0.08} tint="rgba(0,51,102,0.88)"/>

      <div style={{ maxWidth: 720, margin:'0 auto', textAlign:'center', position:'relative' }}>
        <EditableText tag="h2" value={data.titol} onSave={upd('titol')} isAdmin={isAdmin}
          style={{ fontFamily:"'Fredoka',sans-serif", fontSize:'clamp(1.9rem,3.2vw,2.5rem)', fontWeight:600,
            color:'#fff', marginBottom:'0.75rem' }}/>
        <EditableText tag="p" value={data.subtitol} onSave={upd('subtitol')} isAdmin={isAdmin} multiline
          style={{ fontSize:'1.05rem', color:'rgba(255,255,255,0.75)', marginBottom:'2.5rem' }}/>

        <div className="col-2" style={{
          display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1.25rem',
        }}>
          <ContactCard icon={<Mail size={22}/>} label="Correu">
            {isAdmin
              ? <EditableText tag="span" value={data.email} onSave={upd('email')} isAdmin={isAdmin}
                  style={{ color:C.blau, fontWeight:700 }}/>
              : <a href={`mailto:${data.email}`} style={{ color:C.blau, fontWeight:700, textDecoration:'none' }}>
                  {data.email}
                </a>
            }
          </ContactCard>
          <ContactCard icon={<AtSign size={22}/>} label="Instagram">
            {isAdmin
              ? <EditableText tag="span" value={data.instagram} onSave={upd('instagram')} isAdmin={isAdmin}
                  style={{ color:'#c2185b', fontWeight:700 }}/>
              : <a href={`https://www.instagram.com/${data.instagram}/`}
                  target="_blank" rel="noopener noreferrer"
                  style={{ color:'#c2185b', fontWeight:700, textDecoration:'none' }}>
                  @{data.instagram}
                </a>
            }
          </ContactCard>
        </div>
      </div>
    </div>
  );
}

function ContactCard({ icon, label, children }) {
  return (
    <div style={{
      background:'#fff', borderRadius:C.radiusSm, padding:'1.85rem 1.3rem',
      boxShadow:'0 14px 34px rgba(0,0,0,0.18)',
      transition:'transform 0.25s',
    }}
      onMouseEnter={e => e.currentTarget.style.transform='translateY(-4px)'}
      onMouseLeave={e => e.currentTarget.style.transform='none'}
    >
      <div style={{ color:C.blau, marginBottom:'0.6rem', display:'flex', justifyContent:'center' }}>{icon}</div>
      <div style={{ fontSize:'0.72rem', fontWeight:700, color:C.textClar,
        marginBottom:'0.4rem' }}>{label}</div>
      <div style={{ fontSize:'0.95rem', wordBreak:'break-word' }}>{children}</div>
    </div>
  );
}